import { Award, DollarSign, GraduationCap } from 'lucide-react'
import { useEffect, useState } from 'react'

import MultiQueryPageLoader from '@/app/common/components/MultiQueryPageLoader.tsx'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shad/ui/tabs'
import { selectSelectedProgram } from '@/storage/programs/slice.ts'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { PursesManagement } from './_components/purses-management/purses-management.tsx'
import ScholarshipAwarding from './_components/scholarship-awarding/scholarship-awarding.tsx'
import { StudentManagement } from './_components/student-management/student-management.tsx'
import { PURSE_KEYS } from '../../../queries/program_purses/keys.ts'
import { FundingSourceSchema } from './schema.ts'
import AwardHistoryPage from './_components/award-history/award-history-page.tsx'
import { useProgramPursesQuery } from '@/queries/program_purses/useProgramPursesQuery.ts'

export default function ProgramPurseSettings() {
  // Read initial tab from URL hash, fallback to 'purses'
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabs = [
      'purses',
      'students',
      'scholarship-awards',
      'award-history',
    ]
    return validTabs.includes(hash) ? hash : 'purses'
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)
  const programId = useSelector(selectSelectedProgram).id

  // Update URL hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab
  }, [activeTab])

  // Listen for hash changes (e.g., browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '')
      const validTabs = [
        'purses',
        'students',
        'scholarship-awards',
        'award-history',
      ]
      if (validTabs.includes(hash)) {
        setActiveTab(hash)
      }
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  const pursesQuery = useProgramPursesQuery({ programId })

  const purseSourceQuery = useQuery({
    queryFn: async () => {
      const response = await axios.get(`/purse_source_types`)
      return z.array(FundingSourceSchema).parse(response.data)
    },
    queryKey: PURSE_KEYS.PURSE_SOURCE_TYPES,
  })

  return (
    <MultiQueryPageLoader queries={[pursesQuery, purseSourceQuery] as const}>
      {([purses, purseSource]) => {
        return (
          <div className="min-h-screen h-full bg-gray-50/50 text-gray-800">
            <div className="container mx-auto p-6 space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Scholarship Administration
                  </h1>
                  <p className="text-gray-600">
                    Manage program purses and student awards
                  </p>
                </div>
              </div>

              {/* Main Navigation Tabs */}
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full "
              >
                <TabsList className="grid w-full grid-cols-4 mb-10">
                  <TabsTrigger value="purses" className="gap-2">
                    <DollarSign className="h-4 w-4" />
                    Program Purses
                  </TabsTrigger>
                  <TabsTrigger value="students" className="gap-2">
                    <GraduationCap className="h-4 w-4" />
                    Student Management
                  </TabsTrigger>
                  <TabsTrigger value="award-history" className="gap-2">
                    <Award className="h-4 w-4" />
                    Award History
                  </TabsTrigger>
                  <TabsTrigger className="gap-2" value="scholarship-awards">
                    <Award className="h-4 w-4" />
                    Scholarship Awarding
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="purses" className="space-y-6">
                  <PursesManagement purses={purses} purseSource={purseSource} />
                </TabsContent>

                <TabsContent value="students" className="space-y-6">
                  <StudentManagement purses={purses} />
                </TabsContent>
                <TabsContent value="scholarship-awards" className="space-y-6">
                  <ScholarshipAwarding programId={programId} purses={purses} />
                </TabsContent>
                <TabsContent value="award-history" className="space-y-6">
                  <AwardHistoryPage purses={purses} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )
      }}
    </MultiQueryPageLoader>
  )
}
